<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
});

defineEmits(['select']);

const { t } = useI18n();

const STATUS_CLASS = {
  draft: 'bg-n-alpha-1 text-n-slate-11',
  active: 'bg-n-teal-3 text-n-teal-11',
  on_hold: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-blue-3 text-n-blue-11',
  cancelled: 'bg-n-ruby-3 text-n-ruby-11',
};

const HEALTH_DOT = {
  green: 'bg-n-teal-9',
  amber: 'bg-n-amber-9',
  red: 'bg-n-ruby-9',
  unknown: 'bg-n-slate-7',
};

const displayId = computed(
  () => `PRJ-${props.project.display_id ?? props.project.id}`
);

const statusKey = computed(() => props.project.status || 'draft');
const statusClass = computed(
  () => STATUS_CLASS[statusKey.value] || STATUS_CLASS.draft
);
const statusLabel = computed(() =>
  t(`PROJECTS.STATUS.${statusKey.value.toUpperCase()}`)
);

const healthKey = computed(() => props.project.health || 'unknown');
const healthDot = computed(
  () => HEALTH_DOT[healthKey.value] || HEALTH_DOT.unknown
);
const healthLabel = computed(() =>
  t(`PROJECTS.HEALTH.${healthKey.value.toUpperCase()}`)
);

const progress = computed(() => {
  const value = Number(props.project.progress) || 0;
  return Math.max(0, Math.min(100, Math.round(value)));
});

const empresaName = computed(
  () => props.project.empresa?.name || props.project.empresa_name || ''
);

const ownerName = computed(
  () => props.project.owner?.name || props.project.owner_name || ''
);
</script>

<template>
  <div
    class="flex items-center gap-3 px-6 py-2.5 transition-colors cursor-pointer group hover:bg-n-alpha-1"
    role="button"
    tabindex="0"
    @click="$emit('select', project)"
    @keyup.enter="$emit('select', project)"
  >
    <!-- Display id -->
    <span
      class="font-mono text-xs shrink-0 w-16 text-n-slate-10"
      :title="displayId"
    >
      {{ displayId }}
    </span>

    <!-- Name + empresa -->
    <div class="flex flex-col min-w-0 w-56 shrink-0">
      <span class="text-sm font-medium truncate text-n-slate-12">
        {{ project.name }}
      </span>
      <span v-if="empresaName" class="text-xs truncate text-n-slate-10">
        {{ empresaName }}
      </span>
    </div>

    <!-- Status pill -->
    <span
      class="hidden sm:inline-flex items-center px-2 py-0.5 text-xs rounded-md shrink-0 w-24"
      :class="statusClass"
    >
      {{ statusLabel }}
    </span>

    <!-- Health RAG dot -->
    <span
      class="hidden md:flex items-center gap-1.5 text-xs shrink-0 w-24 text-n-slate-11"
      :title="healthLabel"
    >
      <span class="rounded-full size-2.5 shrink-0" :class="healthDot" />
      {{ healthLabel }}
    </span>

    <!-- Progress bar -->
    <div class="flex items-center flex-1 min-w-0 gap-2">
      <div class="flex-1 h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
        <div
          class="h-full rounded-full bg-n-brand"
          :style="{ width: `${progress}%` }"
        />
      </div>
      <span
        class="text-xs tabular-nums shrink-0 w-9 text-right text-n-slate-10"
      >
        {{ progress }}%
      </span>
    </div>

    <!-- Owner -->
    <span
      class="hidden lg:block w-28 px-2 text-xs truncate shrink-0 text-n-slate-10"
    >
      {{ ownerName }}
    </span>

    <!-- Affordance -->
    <span
      class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-8 transition-colors group-hover:text-n-slate-10"
    />
  </div>
</template>
